import React from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { AgentSpec } from '../../../types/backend';

interface AgentCalleesProps {
  /** The active sub_agent agents that may be consulted. */
  options: AgentSpec[];
  selected: string[];
  /** A retired agent's row is history and takes no edits (PS-UC-18 4d). */
  readOnly: boolean;
  onChange: (next: string[]) => void;
}

/**
 * The specialists an interactive or batch agent may consult (PS-UC-19). A callee that is no
 * longer offered, a retired one, stays listed while the row names it, so it can be cleared.
 */
const AgentCallees: React.FC<AgentCalleesProps> = ({ options, selected, readOnly, onChange }) => {
  const offered = new Set(options.map((o) => o.agent_id));
  const rows = [
    ...options.map((o) => ({ id: o.agent_id, label: `${o.display_name} (${o.agent_id})` })),
    ...selected.filter((id) => !offered.has(id)).map((id) => ({ id, label: `${id}, not an active specialist` })),
  ];
  const toggle = (id: string, on: boolean) =>
    onChange(on ? [...selected, id] : selected.filter((s) => s !== id));

  return (
    <Box sx={{ px: 3, pt: 1 }}>
      <Typography variant="caption" color="text.secondary" component="div">Specialists it may consult</Typography>
      {rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">No active specialist</Typography>
      )}
      {rows.map((row) => (
        <FormControlLabel
          key={row.id}
          control={(
            <Checkbox
              size="small"
              checked={selected.includes(row.id)}
              disabled={readOnly}
              onChange={(e) => toggle(row.id, e.target.checked)}
            />
          )}
          label={<Typography variant="body2">{row.label}</Typography>}
        />
      ))}
    </Box>
  );
};

export default AgentCallees;
